import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { api } from '../api/client.js';
import { LeaderboardEntry } from '../types/index.js';
import { slideshowConfig, ContentPage } from '../config/slideshow.js';
import './LeaderboardScreen.css';

const STORYLINE_NAMES: Record<string, string> = {
  ghostpay: 'GhostPay',
  identityshift: 'IdentityShift',
  shadowmerchant: 'ShadowMerchant',
};

function formatCurrency(n: number) {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    maximumFractionDigits: 0,
  }).format(n);
}

function getRankIcon(rank: number) {
  if (rank === 1) return '🥇';
  if (rank === 2) return '🥈';
  if (rank === 3) return '🥉';
  return `#${rank}`;
}

interface Props {
  standalone?: boolean;
  onBack?: () => void;
}

export function LeaderboardScreen({ standalone = false, onBack }: Props) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentSlide, setCurrentSlide] = useState('leaderboard');
  const listRef = useRef<HTMLDivElement>(null);

  // Pin to a specific slide via ?pageid= — useful for testing content pages
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const pageId = params.get('pageid');
    if (pageId) {
      const exists = slideshowConfig.contentPages.find(p => p.id === pageId);
      if (exists || pageId === 'leaderboard') setCurrentSlide(pageId);
    }
  }, []);

  // Fetch leaderboard on mount, then poll every 60 seconds for fresh data
  useEffect(() => {
    let cancelled = false;

    const fetchEntries = (initial: boolean) => {
      api.getLeaderboard()
        .then(r => {
          if (cancelled) return;
          setEntries(prev => {
            const newJson = JSON.stringify(r.entries);
            return JSON.stringify(prev) === newJson ? prev : r.entries;
          });
          if (initial) setLoading(false);
        })
        .catch(e => {
          if (cancelled) return;
          if (initial) {
            setError(e.message);
            setLoading(false);
          }
        });
    };

    fetchEntries(true);
    const interval = setInterval(() => fetchEntries(false), 60_000);

    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  // ── Slideshow rotation (standalone only) ────────────────────────────────
  useEffect(() => {
    if (!standalone) return;
    if (new URLSearchParams(window.location.search).get('pageid')) return;

    const slides = ['leaderboard', ...slideshowConfig.contentPages.map(p => p.id)];
    const idx = slides.indexOf(currentSlide);
    const duration = currentSlide === 'leaderboard'
      ? slideshowConfig.leaderboardDuration
      : slideshowConfig.contentPageDuration;

    const timer = setTimeout(() => {
      setCurrentSlide(slides[(idx + 1) % slides.length]);
    }, duration);

    return () => clearTimeout(timer);
  }, [currentSlide, standalone]);

  // ── Auto-scroll (standalone leaderboard only) ───────────────────────────
  useEffect(() => {
    if (!standalone || currentSlide !== 'leaderboard' || !listRef.current) return;

    const el = listRef.current;
    const maxScroll = el.scrollHeight - el.clientHeight;
    if (maxScroll <= 0) return;

    const TICK = 50;
    const scrollStep = maxScroll / (slideshowConfig.leaderboardDuration / TICK);
    let pos = 0;

    const interval = setInterval(() => {
      pos = Math.min(pos + scrollStep, maxScroll);
      el.scrollTop = pos;
      if (pos >= maxScroll) clearInterval(interval);
    }, TICK);

    return () => clearInterval(interval);
  }, [currentSlide, standalone, entries]);

  // ── Back handler ────────────────────────────────────────────────────────
  const handleBack = () => {
    if (onBack) {
      onBack();
    } else {
      navigate('/');
    }
  };

  // ── Content page renderer ────────────────────────────────────────────────
  const contentMotion = {
    initial: { opacity: 0, scale: 0.95 },
    animate: { opacity: 1, scale: 1 },
    exit:    { opacity: 0, scale: 0.95 },
    transition: { duration: 0.5 },
  };

  const renderContentPage = (page: ContentPage) => {
    if (page.type === 'html' && page.html) {
      return (
        <motion.div
          key={page.id}
          {...contentMotion}
          className="ls-content-page"
          style={{ backgroundColor: page.backgroundColor }}
          dangerouslySetInnerHTML={{ __html: page.html }}
        />
      );
    }

    const isRight = page.type === 'split_right';
    const isLeft  = page.type === 'split_left';
    const isSplit = isRight || isLeft;

    const textBlock = (
      <div className={isSplit ? (isRight ? 'ls-split-left' : 'ls-split-right') : 'ls-content-inner'}>
        {page.icon && <div className="ls-content-icon">{page.icon}</div>}
        {page.title && <h2>{page.title}</h2>}
        {page.content && <p>{page.content}</p>}
      </div>
    );

    const videoBlock = page.videoUrl ? (
      <div className={isRight ? 'ls-split-right' : 'ls-split-left'}>
        <video autoPlay muted loop playsInline className="ls-mobile-video">
          <source src={page.videoUrl} type="video/mp4" />
        </video>
      </div>
    ) : null;

    return (
      <motion.div
        key={page.id}
        {...contentMotion}
        className={`ls-content-page${isSplit ? ' ls-split-page' : ''}`}
        style={{ backgroundColor: page.backgroundColor }}
      >
        {isSplit && videoBlock ? (
          <div className="ls-split-content">
            {isRight ? <>{textBlock}{videoBlock}</> : <>{videoBlock}{textBlock}</>}
          </div>
        ) : textBlock}
      </motion.div>
    );
  };

  // ── Loading / error ──────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="ls-screen">
        <div className="ls-center">Loading leaderboard…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ls-screen">
        <div className="ls-center ls-error">
          <p>{error}</p>
          {!standalone && <button className="ls-back-btn" onClick={handleBack}>Back</button>}
        </div>
      </div>
    );
  }

  // ── Main render ──────────────────────────────────────────────────────────
  return (
    <div className="ls-screen">
      <AnimatePresence mode="wait">

        {currentSlide === 'leaderboard' ? (

          <motion.div
            key="leaderboard"
            className="ls-leaderboard-slide"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="ls-header">
              <h1>Today's Top Fraud Analysts</h1>
              <div className="ls-header-date">
                {new Date().toLocaleDateString('en-AU', {
                  weekday: 'long', day: 'numeric', month: 'long', year: 'numeric',
                })}
              </div>
            </div>

            {entries.length === 0 ? (
              <p className="ls-center">No scores yet today. Be the first to play!</p>
            ) : (
              <div className="ls-list" ref={listRef}>
                {entries.map((entry, i) => (
                  <motion.div
                    key={`${entry.playerName}-${entry.timestamp}`}
                    className={`ls-entry${i < 3 ? ' ls-podium' : ''}`}
                    initial={{ x: -40, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    transition={{ delay: i * 0.06 }}
                  >
                    <div className="ls-rank">{getRankIcon(entry.rank)}</div>
                    <div className="ls-name">{entry.playerName}</div>
                    <div className="ls-score">
                      {entry.score}<span className="ls-score-denom">/10</span>
                    </div>
                    <div className="ls-prevented">{formatCurrency(entry.fraudPrevented)}</div>
                    <div className="ls-storyline">
                      {STORYLINE_NAMES[entry.storylineId] ?? entry.storylineId}
                    </div>
                  </motion.div>
                ))}
              </div>
            )}

            <div className="ls-stats">
              <div className="ls-stat">
                <div className="ls-stat-value">{entries.length}</div>
                <div className="ls-stat-label">Players Today</div>
              </div>
              {entries.length > 0 && (
                <div className="ls-stat">
                  <div className="ls-stat-value">{entries[0].score}/10</div>
                  <div className="ls-stat-label">Top Score</div>
                </div>
              )}
              {entries.length > 0 && (
                <div className="ls-stat">
                  <div className="ls-stat-value">
                    {formatCurrency(Math.max(...entries.map(e => e.fraudPrevented)))}
                  </div>
                  <div className="ls-stat-label">Most Fraud Prevented</div>
                </div>
              )}
            </div>

            {!standalone && (
              <motion.button
                className="ls-back-btn"
                onClick={handleBack}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Play Again
              </motion.button>
            )}
          </motion.div>

        ) : (
          slideshowConfig.contentPages
            .filter(p => p.id === currentSlide)
            .map(p => renderContentPage(p))
        )}

      </AnimatePresence>
    </div>
  );
}
